import {
  Component,
  signal,
  OnDestroy,
  input,
  output,
  computed,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  SystemIconsComponent,
  SignalQualityIconComponent,
} from '@shared/components/ui/system-icons';
import {
  DropdownListComponent,
  TDropdownListItem,
} from '@shared/components/ui/dropdown-list';
import { IntlDateTimePipe } from '@shared/pipes';
import type { AccessPointOption } from '@live-pickup/data-access';
import { ThemeSwitcherComponent } from '../theme-switcher/theme-switcher.component';
import { DateTime } from 'luxon';

type SystemStatus = 'online' | 'degraded' | 'offline';

const ALL_GATES_ID = '__all__';

@Component({
  selector: 'lib-live-pickup-status-bar',
  standalone: true,
  imports: [
    SystemIconsComponent,
    SignalQualityIconComponent,
    TranslateModule,
    ThemeSwitcherComponent,
    DropdownListComponent,
    IntlDateTimePipe,
  ],
  templateUrl: './status-bar.component.html',
  styleUrl: './status-bar.component.scss',
})
export class StatusBarComponent implements OnDestroy {
  public readonly systemStatus = input<SystemStatus>('online');
  public readonly waitingInQueue = input<number>(0);
  public readonly lastUpdate = input<DateTime>(DateTime.now());
  public readonly portao = input<string | null>(null);
  public readonly accessPoints = input<AccessPointOption[]>([]);
  public readonly selectedAccessPointId = input<string | null>(null);

  public readonly accessPointChange = output<AccessPointOption | null>();

  // lib-dropdown-list runs `description` through translate, so the "all" entry
  // is an i18n key while raw access-point names pass through unchanged.
  public readonly gateOptions = computed<TDropdownListItem[]>(() => [
    { id: ALL_GATES_ID, description: 'ACCESS.CHECKIN.LIVE_PICKUP.GATE_ALL' },
    ...this.accessPoints().map((ap) => ({
      id: ap.id,
      description: ap.name,
      value: ap,
    })),
  ]);

  public readonly selectedGateOption = computed<TDropdownListItem>(() => {
    const id = this.selectedAccessPointId();
    const options = this.gateOptions();
    if (!id) return options[0];
    return options.find((o) => o.id === id) ?? options[0];
  });

  public onGateSelected(item: TDropdownListItem): void {
    if (!item || item.id === ALL_GATES_ID) {
      this.accessPointChange.emit(null);
      return;
    }
    const match = this.accessPoints().find((ap) => ap.id === item.id);
    this.accessPointChange.emit(match ?? null);
  }

  public readonly currentTime = signal(DateTime.now().toISO());

  private intervalId: ReturnType<typeof setInterval> | null = null;

  readonly statusConfig: Record<
    SystemStatus,
    { color: string; bg: string; label: string }
  > = {
    online: {
      color: 'text-success-foreground',
      bg: 'bg-success-fade',
      label: 'ACCESS.CHECKIN.LIVE_PICKUP.SYSTEM.ONLINE',
    },
    degraded: {
      color: 'text-warning-foreground',
      bg: 'bg-warning-fade',
      label: 'ACCESS.CHECKIN.LIVE_PICKUP.SYSTEM.DEGRADED',
    },
    offline: {
      color: 'text-danger-foreground',
      bg: 'bg-danger-fade',
      label: 'ACCESS.CHECKIN.LIVE_PICKUP.SYSTEM.OFFLINE',
    },
  };

  public readonly config = computed(() => this.statusConfig[this.systemStatus()]);

  constructor() {
    this.intervalId = setInterval(() => this.currentTime.set(DateTime.now().toISO()), 1000);
  }

  ngOnDestroy(): void {
    if (this.intervalId) clearInterval(this.intervalId);
  }
}
